import { getSiteUiCopy } from "@/lib/site-ui-copy";
import { isCcCursorHiddenByDrag } from "@/lib/cc-cursor-drag";
import type { Lang } from "@/lib/types";

/** Primary card links that show the floating Read pill cursor. */
export const CC_CURSOR_READ_SELECTOR = ".cc-cursor-read-target";

/** Floating Read pill rendered by `CcCursorReadPill`. */
export const CC_CURSOR_READ_PILL_SELECTOR = ".cc-cursor-read-pill";

/** Applied to `<html>` while the Read pill is visible (synced in pointer handlers). */
export const CC_CURSOR_READ_ACTIVE_ROOT_CLASS = "cc-cursor-read-active";

/** Matches `_cursor-read-pill.scss` enter/exit duration (ms). */
export const CC_CURSOR_READ_PILL_TRANSITION_MS = 150;

export type CursorReadPointerContext = {
  clientX: number;
  clientY: number;
  pill?: Element | null;
};

export function getCursorReadLabel(locale: Lang): string {
  return getSiteUiCopy(locale).cursorRead;
}

export function isCursorReadTarget(element: Element | null | undefined): boolean {
  if (!element) return false;
  return Boolean(element.closest(CC_CURSOR_READ_SELECTOR));
}

export function isPointerOverVisibleCursorReadPill(
  context: CursorReadPointerContext,
): boolean {
  const pill = context.pill ?? null;
  if (!pill?.classList.contains("cc-cursor-read-pill--visible")) {
    return false;
  }

  const rect = pill.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;

  const { clientX, clientY } = context;
  return (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  );
}

/** Hide the blend-mode cursor dot over Read cards and the Read pill. */
export function shouldHideCursorDot(
  target: Element | null | undefined,
  pointer?: CursorReadPointerContext,
): boolean {
  if (isCcCursorHiddenByDrag()) return true;

  if (isCursorReadTarget(target)) return true;

  if (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains(CC_CURSOR_READ_ACTIVE_ROOT_CLASS)
  ) {
    return true;
  }

  if (pointer && isPointerOverVisibleCursorReadPill(pointer)) {
    return true;
  }

  return false;
}

export function setCursorReadActiveRootClass(active: boolean): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle(CC_CURSOR_READ_ACTIVE_ROOT_CLASS, active);
}

export function isCursorReadEnvironmentSupported(
  finePointer: Pick<MediaQueryList, "matches">,
  reducedMotion: Pick<MediaQueryList, "matches">,
): boolean {
  return finePointer.matches && !reducedMotion.matches;
}
